import { Loading } from '@brighterion/br-ui';
import React, { useState } from 'react';
import Table from 'Components/Table';
import { RoleService } from 'Services';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Column, Row } from 'react-table';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { RowActionDropdown } from 'Components/Table/RowActionDropdown';
import useSingleReportCSV from 'Hooks/query-hooks/reports/useSingleReportCSV';
import { getUser } from 'Selectors';
import useGetRuleQuickFilters from 'Hooks/rule-quickfilters';
import { QF_MY_REPORTS, QF_NEW_ONLY } from 'Hooks/rule-quickfilters/helper';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import styles from './ReportsTable.css';
import DeleteReportModal from '../DeleteReportModal/DeleteReportModal';
import type { ReportRowValues } from '../DeleteReportModal/DeleteReportModal';

type Props = {
    rawData: FlatReportDataItem[];
    rawColumns: Column<FlatReportDataItem>[];
    isLoading?: boolean;
    reportType: ReportType;
};

const ReportsTable = ({ rawData, rawColumns, isLoading = false, reportType }: Props) => {
    const history = useHistory();
    const { path } = useRouteMatch();
    const { t } = useTranslation();
    const user = useSelector(getUser);
    const data = React.useMemo(() => rawData, [rawData]);
    const [deleteReportIsOpen, setDeleteReportIsOpen] = useState(false);
    const [rowValues, setRowValues] = useState<ReportRowValues>({ id: -1, name: '', createdAt: '', createdBy: '' });
    const handleRowClick = (row: Row<FlatReportDataItem>) => {
        switch (reportType) {
            case 'merchantsummary': {
                trackEvent({
                    eventType: EventTypes.track,
                    eventName: EventNames.click,
                    payload: {
                        clickIdentifier: 'Merchant Summary Reports - View Report',
                        text: 'View Merchant Summary Report'
                    }
                });
                break;
            }
            case 'performance': {
                trackEvent({
                    eventType: EventTypes.track,
                    eventName: EventNames.click,
                    payload: {
                        clickIdentifier: 'Rule Performance Reports - View Report',
                        text: 'View Rule Performance Report'
                    }
                });
                break;
            }
            default: {
                break;
            }
        }
        history.push(`${path}/${reportType}/${row?.original?.id}`);
    };
    const trackExportCsv = () => {
        let clickIdentifier = '';
        let text = '';

        if (reportType === 'merchantsummary') {
            clickIdentifier = 'Merchant Summary Reports - Export CSV';
            text = 'Merchant Summary Menu Export CSV';
        } else if (reportType === 'performance') {
            clickIdentifier = 'Rule Performance Reports - Export CSV';
            text = 'Rules Performance Menu Export CSV';
        }

        if (!clickIdentifier || !text) {
            return;
        }
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier,
                text
            }
        });
    };
    const columns = React.useMemo<Column<FlatReportDataItem>[]>(() => {
        const dropdownCol: Column<FlatReportDataItem> = {
            accessor: 'id',
            id: 'take-action-on-report',
            disableSortBy: true,
            width: 40,
            maxWidth: 40,
            renderType: 'overrideCell',
            Cell: ({ value: id, row }: { value: number; row: Row }) => {
                const csv = useSingleReportCSV({ reportId: id });
                return (
                    <>
                        {csv.isInitialLoading ? (
                            <div style={{ marginLeft: '-8px' }}>
                                <Loading size='sm' fillName='orange' />
                            </div>
                        ) : (
                            <RowActionDropdown
                                menuId={`${id}-action-dropdown`}
                                menuActions={
                                    [
                                        {
                                            testId: 'report-export-csv',
                                            label: t('Export CSV'),
                                            handleAction: (event: React.MouseEvent) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                csv.refetch();
                                                trackExportCsv();
                                            }
                                        },
                                        {
                                            testId: 'delete-report',
                                            label: t('Delete'),
                                            handleAction: (event: React.MouseEvent) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                setDeleteReportIsOpen(true);
                                                setRowValues({
                                                    id: row?.original.id,
                                                    name: row?.original?.name,
                                                    createdAt: row?.original?.createdAt,
                                                    createdBy: row?.original?.createdBy,
                                                    source: 'menu'
                                                });
                                            },
                                            style: { color: 'var(--red000)' }
                                        }
                                    ] as any
                                }
                            />
                        )}
                    </>
                );
            }
        };
        if (RoleService.isReportsAdmin()) {
            return [...rawColumns, { ...dropdownCol }];
        }

        return [...rawColumns];
    }, [rawColumns, reportType, t]);

    const quickFilters = useGetRuleQuickFilters({
        quickFilters: [QF_NEW_ONLY.id, QF_MY_REPORTS.id],
        qfOverrides: [
            {
                id: QF_MY_REPORTS.id,
                data: [
                    {
                        id: QF_MY_REPORTS.options.myReports,
                        text: t('My Reports'),
                        options: {
                            conditional: 'eq',
                            // mcconnect uses sid as userId and okta uses email address as userId
                            valueToMatch: user?.userId,
                            fieldToMatch: 'createdBy'
                        }
                    }
                ]
            }
        ],
        initialFilters: [
            {
                id: QF_MY_REPORTS.id,
                activeId: [QF_MY_REPORTS.options.myReports]
            }
        ]
    });

    return (
        <div className={styles.tableContainer}>
            <Table
                tableName={`${reportType}-reports-table`}
                noDataMessage={t('reportListNoDataMsg', { reportType })}
                isLoading={isLoading}
                showMultiSort
                showRichFilter
                showExpandableSearch
                enablePagination
                initialState={{
                    sortBy: [{ id: 'createdAt', desc: true }],
                    globalFilter: {
                        quickFilters: quickFilters.initialQuickFilters
                    }
                }}
                dataTestId={`${reportType}-reports-table`}
                columns={columns}
                data={data}
                clickableRow
                onRowInteractCB={handleRowClick}
                quickFilters={quickFilters.filters}
            />
            <DeleteReportModal
                isOpen={deleteReportIsOpen}
                onClose={() => setDeleteReportIsOpen(false)}
                rowValues={rowValues}
                reportType={reportType}
            />
        </div>
    );
};

export default ReportsTable;
